import React, { useState, useEffect, useContext } from 'react';

import { LiveReserveContext } from '../../LiveLessonReseveContext';


/**
 * プルダウンの候補（option）のフォントサイズ
 *
 * ★ネイティブのプルダウンは【option 自身の font-size】で候補を描く。
 *   select 側に .input-lg（18px）が効いていても option には継承されないため、
 *   閉じているときは大きいのに候補だけ小さい、というちぐはぐな見え方になる
 *   （実機確認 F-1 の指摘）。iOS の自動ズームを避けるため 16px 以上にする。
 * ★これが効くのは Windows Chrome / Android など【ブラウザが候補を描く環境】だけ。
 *   macOS は OS のポップアップで描くため無視される → 下の SELECT_PICKER_CSS で補う。
 */
const OPTION_STYLE: React.CSSProperties = { fontSize: '16px' };

/**
 * 🔴 `appearance: base-select` は【使えない】(2026-09-08 実機で確認)
 *
 * macOS の候補リストを大きくするには、Chrome 135以降の `appearance: base-select` を当てて
 * ブラウザ描画へ切り替える手がある。実際に試したところ **Bootstrap 3 と衝突した**:
 *   `.form-control` が `display:block` と `height:46px`(.input-lg) を強制するため、
 *   base-select が内部で使うレイアウトが崩れ、**プルダウンの矢印(▼)が枠外へ落ちて消える**。
 *   矢印が無いとただの入力欄に見えるので、**対象が高齢のユーザーである本サイトでは
 *   候補が小さいことより危険**と判断して見送った。
 * 使うなら `.form-control`(Bootstrap) をやめて自前のスタイルに置き換える必要があり、
 * 1853 の範囲を超える。→ 別課題の候補。
 */


// 時間選択 
function LiveReserveTime() {

  const {
    isLoading,
    lessonTermMinute,
    user, trialCount,
    lsnMin, setLsnMin,
    isTrial, setIsTrial,
    isLsnSvyAddTicketTerm,
    instId, instructors,
  } = useContext(LiveReserveContext);

  const [isFirstSelected, setIsFirstSelected] = useState<boolean>(false);


  /**
   * 選択中の講師で【予約できる】最長のレッスン時間 (SPEAKOUT-1853)
   *
   * 講師未指定、または一覧に居ない講師（空き枠ゼロ＝どの長さでもリクエスト）は上限なし(null)。
   * 判定に使う open_term_flg はサーバーが講師ごとに返す「連続した空き枠の数」
   * （term_15=1枠 / term_30=2枠連続 / term_45=3枠連続）。
   */
  const getBookableMaxMinute = (): number | null => {
    if(!instId || !instructors) return null;
    const instructor: any = instructors.find((inst: any) => inst.inst_id == instId);
    if(!instructor?.open_term_flg) return null;
    if(instructor.open_term_flg.term_45) return lessonTermMinute*3;
    if(instructor.open_term_flg.term_30) return lessonTermMinute*2;
    if(instructor.open_term_flg.term_15) return lessonTermMinute;
    return null;
  }


  // レッスン時間選択の初期値
  //
  // ★SPEAKOUT-1853 講師が指定されているときは【その講師で予約できる長さ】まで初期値を下げる。
  //   既定は30分（無料2回分／チケット6枚以上）だが、選択中の講師に20分枠の2連続が無いと
  //   日程欄が自動でリクエストへ切り替わる。講師一覧の「予約する」（＝空き枠あり）から
  //   来た生徒が、15分なら予約できるのにいきなりリクエストに落ちてしまうため。
  // 🔴 下げるのは【初期値だけ】。生徒が自分で 30/45 を選んだときは下げない
  //   （枠が無い長さを希望してリクエストできることが本課題の狙いなので、master にあった
  //   「選べない時間が選ばれていたら落とす」補正は撤廃したまま）。
  useEffect(() => {
    if(!isFirstSelected && user && trialCount!=null) {
      // 希望（従来どおり無料回数 → チケット残で決める）
      let minute = lessonTermMinute;
      if(trialCount >= 2) {
        // 30分:無料
        minute = lessonTermMinute*2;
      } else if(trialCount >= 1) {
        // 15分:無料
        minute = lessonTermMinute;
      } else if(user?.ticket >= 6) {
        // 30分:チケット
        minute = lessonTermMinute*2;
      } else {
        // 15分:チケット
        minute = lessonTermMinute;
      }

      // 予約できる長さまで下げる
      const maxMinute = getBookableMaxMinute();
      if(maxMinute !== null && minute > maxMinute) minute = maxMinute;

      // 無料回数で足りるかは【下げた後の長さ】で決める（master の補正と同じ扱い）
      setLsnMin(minute);
      setIsTrial(trialCount >= (minute / lessonTermMinute));

      setIsFirstSelected(true);
    }
  }, [user, trialCount, instId, instructors])


  /**
   * ★SPEAKOUT-1853 レッスン時間の絞り込み（と15分への強制）は【撤廃した】
   *
   * 以前はここで「選択中の講師に20分枠が2つ連続で空いていれば30分を出す」(open_term_flg)
   * という出し分けと、選べない時間が選ばれていたら15分に落とす補正を行っていた。
   * その結果、空き枠の無い講師では15分しか出せず、**枠が無いからリクエストしたいのに
   * 枠が無いせいで「30分でお願いしたい」が言えない**という穴があった。
   *
   * 統合版では日程欄が「選択中の講師×このレッスン時間で予約できる枠があるか」で
   * 自動的に予約／リクエストへ分かれるので、ここで絞り込む理由がなくなった。
   * 15/30/45 は常に出し、枠が無ければ日程欄がリクエストに切り替わる（一本道）。
   * ★残っている出し分けは無料回数(trialCount)とチケット残数だけ＝どちらも予約の可否そのもの。
   */

  // 時間変更
  const handleTime = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    if(!isLoading) {
      const { minute, isTrial } = JSON.parse(e.target.value);
      setLsnMin(parseInt(minute, 10));
      setIsTrial(isTrial);
    }
  }


  return (
    <div>
      <div>
        <label className="control-label" style={{
          margin: '15px 0 10px 0',
          backgroundColor: '#eaffea',
          padding: '0.25em 3em 0.25em 0.5em',
          borderLeft: 'solid 5px #3c8c3c',
        }}>レッスン時間を選択</label>

        <div style={{ marginBottom: '15px' }}>
          <select
            name="lesson_minutes_type"
            className="form-control input-lg"
            id="InputSelect"
            onChange={handleTime}
            value={JSON.stringify({
              minute: lsnMin,
              isTrial: isTrial 
            })}
          >

            {/* 15分/無料 */}
            {trialCount >= 1 &&
              <option style={OPTION_STYLE} value={JSON.stringify({
                minute: lessonTermMinute,
                isTrial: true
              })}>
                {lessonTermMinute}分（無料1回分）
              </option>
            }

            {/* 30分/無料 */}
            {(trialCount >= 2) &&
              <option style={OPTION_STYLE} value={JSON.stringify({
                minute: lessonTermMinute*2,
                isTrial: true
              })}>
                {lessonTermMinute*2}分（無料2回分）
              </option>
            }

            {/* 45分/無料 */}
            {(trialCount >= 3) &&
              <option style={OPTION_STYLE} value={JSON.stringify({
                minute: lessonTermMinute*3,
                isTrial: true
              })}>
                {lessonTermMinute*3}分（無料3回分）
              </option>
            }

            {/* 15分/チケット */}
            {(
              user?.ticket >= 3 ||
              (user?.ticket<3 && trialCount<=0)
            ) &&
              <option style={OPTION_STYLE} value={JSON.stringify({
                minute: lessonTermMinute,
                isTrial: false
              })}>
                {lessonTermMinute}分（3チケット）
              </option>
            }

            {/* 30分/チケット */}
            {(user?.ticket>=6) && (
              <option style={OPTION_STYLE} value={JSON.stringify({
                minute: lessonTermMinute*2,
                isTrial: false
              })}>
                {lessonTermMinute*2}分（6チケット）
              </option>
            )}

            {/* 45分/チケット */}
            {(user?.ticket>=9) && (
              <option style={OPTION_STYLE} value={JSON.stringify({
                minute: lessonTermMinute*3,
                isTrial: false
              })}>
                {lessonTermMinute*3}分（9チケット）
              </option>
            )}

          </select>

          {/* 30分2チケット：アンケートで１チケットプレゼントキャンペーン */}
          {isLsnSvyAddTicketTerm &&
            <p
              className='text-center'
              style={{color: 'red', fontWeight: 'bold'}}
            >
              30分(6チケット)で1チケットGET🎁
              <span className="visible-xs"></span>
              16〜22日の7日間限定キャンペーン中！
            </p>
          }

        </div>
      </div>
    </div>
  );
}
  
export default LiveReserveTime;